<script setup lang="ts">
import { computed, type PropType, onMounted, ref } from 'vue';
import { type FormField } from '@/models/form';
import { valueToHtml } from './RichInputHelper';

const props = defineProps({
  modelValue: String,
  suggestions: {
    type: Object as PropType<FormField[]>
  },
  placeholder: {
    type: String,
    default: 'Enter text here...'
  },
  fontWeight: {
    type: Number,
    default: 500
  },
  visibleBorder: {
    type: Boolean,
    default: false
  }
});

const inputValue = ref(valueToHtml(props.suggestions, props.modelValue));
const showSuggestions = ref(false);
const inputRef = ref<HTMLElement | null>(null);

let lastSearchText: string | null = null;

const currentKey = ref<string | null>(null);
const searchText = ref<string | null>(null);

const filteredSuggestions = computed(() => {
  if (!searchText.value) {
    return props.suggestions || [];
  }
  const finalSearchText = searchText.value.toLowerCase();
  return (props.suggestions || []).filter(item =>
    item.label.toLowerCase().includes(finalSearchText)
  );
});

const getSelectionStart = () => {
  return inputRef.value && inputRef.value.isContentEditable ?
    window.getSelection()?.anchorOffset :
    (inputRef.value as HTMLInputElement).selectionStart;
};

const getValue = () => {
  return inputRef.value && inputRef.value.isContentEditable ?
    window.getSelection()?.anchorNode?.textContent ?? '' :
    (inputRef.value as HTMLInputElement).value;
};

const getLastKeyBeforeCaret = (caretIndex: number) => {
  const [keyData] = ['@']
    .map(key => ({
      key,
      keyIndex: getValue().lastIndexOf(key, caretIndex - 1)
    }))
    .sort((a, b) => b.keyIndex - a.keyIndex);
  return keyData;
};

const getLastSearchText = (caretIndex: number, keyIndex: number) => {
  if (keyIndex !== -1) {
    const text = getValue().substring(keyIndex + 1, caretIndex);
    // If there is a space we close the menu
    if (!/\s/.test(text)) {
      return text;
    }
  }
  return null;
};

const openMenu = (key: string) => {
  if (currentKey.value !== key) {
    currentKey.value = key;
    showSuggestions.value = true;
  }
};

const closeMenu = () => {
  if (currentKey.value != null) {
    currentKey.value = null;
    showSuggestions.value = false;
  }
};

onMounted(() => {
  inputRef.value!.innerHTML = inputValue.value ?? '';
  setTimeout(() => {
    onInput();
  }, 20);
});

const onInput = () => {
  const input = inputRef.value!;
  if (input) {
    inputValue.value = input.innerText;
    emitValueToParent();

    // Set or remove the 'empty' attribute based on content
    if (input && input.textContent?.trim() === '') {
      input.setAttribute('empty', '');
    } else {
      input.removeAttribute('empty');
    }
    const index: number = getSelectionStart() ?? 0;
    if (index >= 0) {
      const { key, keyIndex } = getLastKeyBeforeCaret(index);
      lastSearchText = getLastSearchText(index, keyIndex);
      const text = lastSearchText;
      if (text != null) {
        openMenu(key);
        searchText.value = text;
        return true;
      }
    }
  }

  closeMenu();
  return false;
};

const selectSuggestion = (itemIndex: number) => {
  const input = inputRef.value!;
  const item = props.suggestions![itemIndex];
  const mentionText = `@${item.label}`;

  if (input && input.isContentEditable) {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);

      // Move the range back to account for the text that will be replaced
      range.setStart(
        range.startContainer,
        range.startOffset -
          (currentKey.value?.length || 0) -
          (lastSearchText ? lastSearchText.length : 0)
      );
      range.deleteContents();

      // Create the span element for the mention
      const span = document.createElement('span');
      span.classList.add('mention');
      span.textContent = mentionText;

      // Insert the mention into the content
      range.insertNode(span);

      // Ensure the cursor moves to after the mention
      const newRange = document.createRange();
      newRange.setStartAfter(span);
      newRange.insertNode(document.createTextNode(' '));
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);

      // Handle the rest of the text
      input.focus();

      inputValue.value = input.innerText;
      emitValueToParent();
    }
  }

  closeMenu();
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    showSuggestions.value = false;
  }
};

const emit = defineEmits(['update:modelValue', 'change']);

const emitValueToParent = () => {
  let value = inputValue.value;
  for (let i = 0; i < (props.suggestions?.length ?? 0); i++) {
    const field = props.suggestions![i];
    value = value?.replace(
      `@${field.label}`,
      `@${field.identifier || field.internalId}`
    );
  }
  emit('update:modelValue', value);
};

const borderColor = computed(() => props.visibleBorder ? `hsla(0, 0%, 0%, 0.25)` : `transparent`);
</script>

<template>
  <div class="rich-input">
    <div class="input-area"
      contenteditable="true"
      @input="onInput"
      @keydown="onKeyDown"
      @blur="closeMenu"
      ref="inputRef"
      :placeholder="placeholder"/>
    <ul v-if="showSuggestions" class="suggestions">
      <li v-for="(suggestion, index) in filteredSuggestions"
        :key="index"
        @mousedown="
          selectSuggestion(suggestions!.findIndex((item) => item == suggestion))
        ">
        {{ suggestion.label }}
      </li>
    </ul>
    <div class="empty-suggestion" v-if="suggestions && suggestions!.length > 0 && filteredSuggestions.length === 0">
      Cannot find the field with entered text...
    </div>
  </div>
</template>

<style scoped lang="scss">
.rich-input {
  position: relative;
  width: 100%;

  .input-area {
    padding: 8px 4px;
    border: 1px solid v-bind(borderColor);
    border-radius: 4px;
    white-space: pre-wrap;
    word-wrap: break-word;
    outline: none;
    color: black;
    font-weight: v-bind(fontWeight);
    font-size: 15px;
    line-height: 18px;

    &:hover {
      border: 1px solid hsla(0, 0%, 0%, 0.25);
    }

    &:focus {
      border: 1px solid hsla(0, 0%, 0%, 0.25);
    }
  }

  :deep(.mention) {
    color: black;
    font-weight: v-bind(fontWeight + 100);
    background-color: hsl(45, 13%, 94%);
    padding: 2px 4px;
    display: inline-flex;
    border-radius: 4px;
    align-items: center;
  }

  .input-area[empty]:before {
    content: attr(placeholder);
    color: #888; /* Placeholder text color */
    position: absolute;
    pointer-events: none;
    white-space: pre-wrap;
  }
}
.suggestions {
  list-style: none;
  margin: 0;
  margin-top: 4px;
  padding: 0;
  background: white;
  border: 1px solid hsla(0, 0%, 0%, 0.25);
  border-radius: 4px;
  position: absolute;
  z-index: 1000;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
}
.suggestions li {
  padding: 8px;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }
}
.empty-suggestion {
  margin-top: 4px;
  padding: 8px;
  background: white;
  border: 1px solid hsla(0, 0%, 0%, 0.25);
  border-radius: 4px;
  position: absolute;
  z-index: 1000;
  width: 100%;
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05),
    0 1px 3px 1px rgba(15, 15, 15, 0.05);
}
</style>
